import React from 'react';
import { Button, CheckboxInput, TextInput, Select } from 'ui-toolkit';
import { createOnChangeHandlerGenerator } from '../utilities/createOnChangeHandlerGenerator';
import '../../../scss/_cost-data.scss';
import CdNumberInput from '../CdNumberInput.styles';
import IHasItemDataProps from './IHasItemDataProps';
import IHasItemEventProps from './IHasItemEventProps';
import IItemProps from './IItemProps';
import styled from 'styled-components';
import {
  AlignedRow,
  RowNumber,
  NameContent,
  ToContent,
  PayeeContent,
  AmountContent,
  NettedOutContent,
  EmptyContent,
  LenderPaidContent,
  DeleteContent,
} from './StyledCdItemCells';
import { lenderPaidOptions } from '../utilities/lenderPaidOptions';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { StyledFontAwesomeThatCanBeDisabled } from '../../shared/permissions/StyledFontAwesomeThatCanBeDisabled';
import { IPayoffViewItem } from 'ui-toolkit/cdItems/interfaces/ICDViewItem';
import { generateDefaultViewItem } from '../../../ui-toolkit/cdItems/data/generateDefaultViewItem';
import { eLenderPaid } from '../../../ui-toolkit/enums/eLenderPaid';

const FullFormTextInput = styled(TextInput)`
  flex: 1;
`;

interface IPayoff
  extends IItemProps,
    IHasItemDataProps<IPayoffViewItem>,
    Required<IHasItemEventProps<IPayoffViewItem>> {}

const FullWidthSelect = styled(Select)`
  width: 100%;
`;

export default class Payoff extends React.Component<IPayoff> {
  constructor(props: IPayoff) {
    super(props);
    this.onRemoveEvent = this.onRemoveEvent.bind(this);
  }

  public render() {
    const { data, onChange, showSeller, hasEditPermissions } = this.props;
    const { buyer_at_closing, label, payee, seller_at_closing, netted_out, lender_paid } = data;
    const amount = showSeller ? seller_at_closing : buyer_at_closing;

    const generateDefaultOnChangeHandler = createOnChangeHandlerGenerator(data, onChange);
    const labelOnChange = generateDefaultOnChangeHandler('label');
    const payeeOnChange = generateDefaultOnChangeHandler('payee');
    const sellerAmountOnChange = generateDefaultOnChangeHandler('seller_at_closing');
    const buyerAmountOnChange = generateDefaultOnChangeHandler('buyer_at_closing');
    const onNettedOutChange = (checked: boolean) =>
      onChange({ ...generateDefaultViewItem(), ...data, netted_out: checked, dirty: true });
    const onLenderPaidChange = (lenderPaid: { value: eLenderPaid; label: string }) =>
      onChange({ ...generateDefaultViewItem(), ...data, lender_paid: lenderPaid.value, dirty: true });

    const amountOnChange = showSeller ? sellerAmountOnChange : buyerAmountOnChange;
    return (
      <AlignedRow showPayoff={true} showSeller={false} showLenderPaid={true} showNettedOut={true}>
        <RowNumber />
        <NameContent>
          <FullFormTextInput
            onChange={labelOnChange}
            placeholder="Name"
            value={label}
            readOnly={!hasEditPermissions}
            debounceOnValueChange
          />
        </NameContent>
        <ToContent>to</ToContent>
        <PayeeContent>
          <FullFormTextInput
            onChange={payeeOnChange}
            placeholder="Payee"
            value={payee}
            readOnly={!hasEditPermissions}
            debounceOnValueChange
          />
        </PayeeContent>
        <EmptyContent />
        <AmountContent>
          <CdNumberInput
            name={showSeller ? 'seller_at_closing' : 'buyer_at_closing'}
            mode="currency"
            onChange={amountOnChange}
            value={amount}
            readOnly={!hasEditPermissions}
            debounceOnValueChange
          />
        </AmountContent>
        <NettedOutContent className="cost-data__content-cell cost-data__content-netted-out">
          <CheckboxInput
            name="netted_out"
            onChange={onNettedOutChange}
            value={netted_out}
            readOnly={!hasEditPermissions}
          />
        </NettedOutContent>
        <LenderPaidContent className="cost-data__content-cell cost-data__content-lender-paid">
          <FullWidthSelect
            options={lenderPaidOptions()}
            name="lender_paid"
            onChange={onLenderPaidChange}
            value={lenderPaidOptions().find((option) => option.value === lender_paid)}
            isDisabled={!hasEditPermissions}
          />
        </LenderPaidContent>
        <DeleteContent className="cost-data__content-cell cost-data__content-delete">
          <Button intent="danger" isInline onClick={this.onRemoveEvent} disabled={!hasEditPermissions}>
            <StyledFontAwesomeThatCanBeDisabled icon={faTrash} disabled={!hasEditPermissions} />
          </Button>
        </DeleteContent>
      </AlignedRow>
    );
  }

  private onRemoveEvent() {
    this.props.onRemove(this.props.data);
  }
}
